import { BrowserRouter } from 'react-router-dom';
import ScheduleProviderCardUtils from '@src/js/provider-card-utils.js';
import { AppointmentTypes } from '@src/js/constants.js';
import { SESSION_STORAGE_KEYS } from '@js/constants/js-constants.es6.js';
import ConfirmationUtils, { VISIT_TYPE_ELEMENT_IDS } from '@js/confirmation-utils.es6.js';
import ReactDOM from 'react-dom';
import LuminisDatePicker from '@src/components/datepicker/LuminisDatePicker.jsx';
import * as Luxon from 'luxon';
import React from 'react';
import ApiUtils from '@js/api-utils.es6.js';
import { LuminisServiceUtils } from '@js/utils/utils-imports.es6.js';

export default class DynamicAppointmentFormUtils {
  static async buildMnemonicSelect(mnemonicJson, providerStartAndEndDates, currentMnemonic, providerAllSlots) {
    const mnemonicList = [];
    const exclusionMnemonics = await LuminisServiceUtils.getExclusionMnemonics();

    if (currentMnemonic === null) {
      mnemonicList.push('');
    }

    // providerAllSlots is a cached version of availabilities that includes the
    // currently selected slot even though it no longer pulls as available
    // because it's now reserved
    const numberOfAvailabilitiesBeforeReservation = providerAllSlots ? providerAllSlots.departmentAvailabilities.map(x => x.availabilities).flat().length : 0;
    const isLastAvailableSlot = numberOfAvailabilitiesBeforeReservation === 1;

    // mnemonicJson does not include the currently selected slot
    const hasRemainingAvailability = mnemonicJson.length > 0;

    if (isLastAvailableSlot && !hasRemainingAvailability) {
      // we took the last slot and need to build our own mnemonic json
      mnemonicJson = [{
        visitReasonMnemonic: currentMnemonic,
        departmentAvailabilities: providerAllSlots.departmentAvailabilities
      }];
    }

    for (const mnemonicResult of mnemonicJson) {
      let doMnemonicsMatch = false;
      let didKeepReservedSlots = false;
      if (currentMnemonic) {
        doMnemonicsMatch = mnemonicResult.visitReasonMnemonic.toLowerCase() === currentMnemonic.toLowerCase();
        // because the original mnemonic also includes a time slot for the reserved slot, use that for the
        // currently selected mnemonic instead of what the api returns.
        if (doMnemonicsMatch && Object.keys(providerAllSlots.departmentAvailabilities).length !== 0) {
          mnemonicResult.departmentAvailabilities = providerAllSlots.departmentAvailabilities;
          didKeepReservedSlots = true;
        }
      }

      // if not the currently selected passed in mnemonic, make sure it's not in the list of exclusion mnemonics
      if (!didKeepReservedSlots && exclusionMnemonics.includes(mnemonicResult.visitReasonMnemonic)) {
        continue;
      }

      const humanReadableMnemonic = await ApiUtils.makeMnemonicHumanReadable(mnemonicResult.visitReasonMnemonic);
      mnemonicList.push(
        {
          text: humanReadableMnemonic,
          id: mnemonicResult.visitReasonMnemonic,
          selected: doMnemonicsMatch,
          mnemonicJson: mnemonicResult,
          startDate: providerStartAndEndDates.startDate,
          endDate: providerStartAndEndDates.endDate
        }
      );
    }
    return mnemonicList;
  }

  static initConditionSelect($, conditionSelector, mnemonicList, eventTrigger, eventAdditionalParam, placeholder = null) {
    const $condition = $(conditionSelector);

    const enabled = mnemonicList.length > 1;
    $condition.prop("disabled", !enabled);

    const options = {
      data: mnemonicList,
    };
    if (placeholder) {
      options.placeholder = placeholder;
    }
    $condition.html('').select2(options).trigger('change');

    $condition.on('select2:select', e => {
      const event = e.params.data;
      eventTrigger(event, eventAdditionalParam);
    });
  }

  static setupMnemonicSlots(event) {
    // grab the mnemonic from the event
    // get the new start and end date
    const startDate = event.startDate;
    const endDate = event.endDate;
    let providerAllSlots = {};
    providerAllSlots.departmentAvailabilities = event.mnemonicJson.departmentAvailabilities;
    providerAllSlots = ScheduleProviderCardUtils.mapTimeSlotsToDays(
      providerAllSlots, startDate, endDate, {}, {});
    providerAllSlots = ScheduleProviderCardUtils.addFilteredSlots(providerAllSlots);
    sessionStorage.setItem('providerAllSlots', JSON.stringify(providerAllSlots));
    return providerAllSlots;
  }

  static disableVisitTypeBasedOnMnemonic(providerAllSlots) {
    DynamicAppointmentFormUtils.showHideLocationsBasedOnVisitType();
    ConfirmationUtils.disableVisitTypeIfEmpty(providerAllSlots);
  }

  static visitTypeOnChange(providerAllSlots, showInline) {
    DynamicAppointmentFormUtils.disableVisitTypeBasedOnMnemonic(providerAllSlots);
    document.getElementById(VISIT_TYPE_ELEMENT_IDS.IN_PERSON).addEventListener('click', () => {
      // this will be reset once the user chooses a new slot. We just need to make it not equal Telemedicine.
      sessionStorage.setItem('appointmentType', AppointmentTypes.IN_PERSON);
      ConfirmationUtils.updateHighlightRadio();
      DynamicAppointmentFormUtils.showHideLocationsBasedOnVisitType();
      DynamicAppointmentFormUtils.clearDatePickerAndVisitType(showInline, providerAllSlots);
    });
    document.getElementById(VISIT_TYPE_ELEMENT_IDS.TELEHEALTH).addEventListener('click', () => {
      sessionStorage.setItem('appointmentType', AppointmentTypes.TELEMEDICINE);
      ConfirmationUtils.updateHighlightRadio();
      DynamicAppointmentFormUtils.showHideLocationsBasedOnVisitType();
      DynamicAppointmentFormUtils.clearDatePickerAndVisitType(showInline, providerAllSlots);
    });
  }

  static clearDatePickerAndVisitType(showInline, providerAllSlots) {
    sessionStorage.setItem('reservedTimeSlot', '');
    DynamicAppointmentFormUtils.updateTime('');
    DynamicAppointmentFormUtils.renderDatePicker(showInline, providerAllSlots);
  }

  static updateTime(slotSelected) {
    const timeBox = document.querySelector('.day-time-box .time-box');
    const timeJoiner = document.querySelector(
      '.field--name-field-time-joining-text .field__item',
    );
    if (timeBox && timeJoiner) {
      timeBox.innerHTML = `${timeJoiner.innerHTML} ${DynamicAppointmentFormUtils.getHoursFromDate(slotSelected)}`;
    }
  }

  static getHoursFromDate(slotSelected) {
    if (slotSelected) {
      const date = new Date(slotSelected);
      return date.toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
      });
    }
    return '';
  }

  static showHideLocationsBasedOnVisitType() {
    const locationsBox = document.querySelector('.location-box-wrapper');
    const telehealthBox = document.querySelector('.telehealth-box-wrapper');
    if (DynamicAppointmentFormUtils.isVisitTypeTelehealth()) {
      locationsBox.classList.add('hidden');
      telehealthBox.classList.remove('hidden');
    } else {
      locationsBox.classList.remove('hidden');
      telehealthBox.classList.add('hidden');
    }
  }

  static isVisitTypeTelehealth() {
    return sessionStorage.getItem('appointmentType') === AppointmentTypes.TELEMEDICINE;
  }

  static renderDatePicker(showInline, providerAllSlots) {
    // get dates for current epicDepartmentId
    const epicDepartmentId = sessionStorage.getItem('epicDepartmentId');
    const chooseADayAndTimeText = document.querySelector('.field--name-field-choose-a-day-time .field__item').textContent;
    const closeCalendarText = document.querySelector('.field--name-field-close-calendar .field__item').textContent;
    const doneText = document.querySelector('.field--name-field-done .field__item').textContent;
    let continueText = document.querySelector('.field--name-field-continue-scheduling-label .field--name-field-label .field__item');
    continueText = continueText ? continueText.textContent : '';
    const timesText = document.querySelector('.field--name-field-times- .field__item').textContent;
    const isVisitTypeTelehealth = DynamicAppointmentFormUtils.isVisitTypeTelehealth();
    let selectedDayAndTimeText = document.querySelector('.field--name-field-selected-day-time-label .field--name-field-label .field__item');
    if (selectedDayAndTimeText) {
      selectedDayAndTimeText = selectedDayAndTimeText.textContent;
    }
    const currentMnemonic = document.querySelector('#field-form-element-condition').value;

    let selectedDayAndTimeTextJoiner = document.querySelector('.field--name-field-selected-day-time-joiner .field--name-field-label .field__item');
    if (selectedDayAndTimeTextJoiner) {
      selectedDayAndTimeTextJoiner = selectedDayAndTimeTextJoiner.textContent;
    }

    if (epicDepartmentId || isVisitTypeTelehealth) {
      let currLocation = DynamicAppointmentFormUtils.getCurrentLocationDept(epicDepartmentId, providerAllSlots);

      let timesList;

      if (isVisitTypeTelehealth) {
        timesList = providerAllSlots.daysWithTelehealth;
      } else {
        timesList = currLocation.daysWithoutTelehealth;
      }

      if (timesList) {
        const maxDate = timesList[timesList.length - 1][0];
        const minDate = timesList[0][0];
        const selectedTimeSlot = sessionStorage.getItem('reservedTimeSlot');
        const selectedDate = selectedTimeSlot ? selectedTimeSlot.split('T')[0] : '';
        const selectedDateObj = selectedDate ? DynamicAppointmentFormUtils.convertFromISOtoJSDate(selectedDate) : undefined;
        const excludedDates = DynamicAppointmentFormUtils.getExcludedDates(timesList);

        let originalTimeSlots = [];
        timesList.forEach(times => {
          if (times[0] === selectedDate) {
            originalTimeSlots = times[1];
          }
        });

        const id = `${currentMnemonic}-${epicDepartmentId}-${isVisitTypeTelehealth}`;

        ReactDOM.render(
          <LuminisDatePicker
            selectedDate={selectedDateObj}
            minDate={DynamicAppointmentFormUtils.convertFromISOtoJSDate(minDate)}
            maxDate={DynamicAppointmentFormUtils.convertFromISOtoJSDate(maxDate)}
            excludedDates={excludedDates}
            timesList={timesList}
            originalTimeSlots={originalTimeSlots}
            slotSelected={sessionStorage.getItem('reservedTimeSlot')}
            chooseADayAndTimeText={chooseADayAndTimeText}
            closeCalendarText={closeCalendarText}
            doneText={doneText}
            timesText={timesText}
            isTelehealthSelected={isVisitTypeTelehealth}
            key={id} // needed to force a destroy and re-render when location changes.
            id={id}
            showInline={showInline}
            continueText={continueText}
            selectedDayAndTimeText={selectedDayAndTimeText}
            selectedDayAndTimeTextJoiner={selectedDayAndTimeTextJoiner}
            currentMnemonic={currentMnemonic}
          />,
          document.getElementById('date-box')
        );
      }
    } else {
      // clear datepicker

      const id = `${currentMnemonic}-${epicDepartmentId}`;

      ReactDOM.render(
        <BrowserRouter>
          <LuminisDatePicker
            selectedDate={undefined}
            minDate={new Date()}
            maxDate={new Date()}
            excludedDates={[new Date()]}
            timesList={[]}
            originalTimeSlots={[]}
            slotSelected={sessionStorage.getItem('reservedTimeSlot')}
            chooseADayAndTimeText={chooseADayAndTimeText}
            closeCalendarText={closeCalendarText}
            doneText={doneText}
            timesText={timesText}
            isTelehealthSelected={false}
            key={id} // needed to force a destroy and re-render when location changes.
            id={id}
            showInline={showInline}
            continueText={continueText}
            currentMnemonic={currentMnemonic}
          />
        </BrowserRouter>,
        document.getElementById('date-box'));
    }
  }

  static getCurrentLocationDept(epicDepartmentId, providerAllSlots) {
    let currLocation = {};
    providerAllSlots.departmentAvailabilities.forEach(dept => {
      if (dept.department.id === epicDepartmentId) {
        currLocation = dept;
      }
    });
    return currLocation;
  }

  static getExcludedDates(timesList) {
    const excludedList = [];
    timesList.forEach(slotArray => {
      if (slotArray[1].length === 0) {
        excludedList.push(DynamicAppointmentFormUtils.convertFromISOtoJSDate(slotArray[0]));
      } else {
        if (slotArray[1][0].slot === "") {
          excludedList.push(DynamicAppointmentFormUtils.convertFromISOtoJSDate(slotArray[0]));
        }
      }
    });

    return excludedList;
  }

  /**
   * Central timezone fix. See https://github.com/moment/luxon/issues/655 for
   * why this is necessary.
   */
  static convertFromISOtoJSDate(isoDate) {
    const luxonDate = Luxon.DateTime.fromISO(isoDate, { zone: "America/New_York" }).setZone("local", { keepLocalTime: true });
    return luxonDate.toJSDate();
  }

  static redirectToScheduleOnline(npi, currentMnemonic, epicDepartmentId) {
    const scheduleOnlineLink = document.querySelector(
      '.field--name-field-scheduling-page-link a',
    );

    if (scheduleOnlineLink) {
      window.location = `${scheduleOnlineLink.href}?npi=${npi}&mnemonic=${currentMnemonic}&epicDepartmentId=${epicDepartmentId}`;
    }
  }
}
